import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { CONTACT_FORM_SCHEMA } from "../../utils/PatientPortal/Validators/AvailabilityValidation";
import {
  contactFormInitValues,
  contactReasons,
} from "../../constant/patientPortalConstants";
import { useAuth0 } from "react-auth0-spa";
import { ApplicationStages } from "constant/AppConstants";
import axios from "axios";

const maxSubjectLength = 60;
const maxDescriptionLength = 1024;

interface ContactFormValues {
  firstName: string;
  lastName: string;
  contactReason: string;
  subject: string;
  description: string;
}

interface ContactFormProps {
  open: boolean;
  onClose: () => void;
  onSent: () => void;
}

const ContactForm: React.FC<ContactFormProps> = ({ open, onClose, onSent }) => {
  const { user, getTokenSilently } = useAuth0();

  const [showForm, setForm] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [loading, setloading] = React.useState<boolean>(false);

  const methods = useForm<ContactFormValues>({
    defaultValues: contactFormInitValues,
    resolver: yupResolver(CONTACT_FORM_SCHEMA),
    mode: "all",
    shouldFocusError: true,
    shouldUnregister: false,
    reValidateMode: "onChange",
  });
  const { errors } = methods.formState;

  // A failed send's message doesn't follow the family into the next visit
  // to the drawer; the unsent text itself is kept.
  useEffect(() => {
    if (!open) setError(false);
  }, [open]);

  const submitRequest = async () => {
    const requestFlightPayload = {
      firstName: methods.getValues("firstName"),
      lastName: methods.getValues("lastName"),
      email: user.email,
      contactReason: methods.getValues("contactReason"),
      subject: methods.getValues("subject"),
      description: methods.getValues("description"),
    };

    const flightUrl =
      process.env.REACT_APP_STAGE === ApplicationStages.local
        ? `http://localhost:8000/sendContactFormData`
        : `${process.env.REACT_APP_BACKEND_API}/sendContactFormData`;

    try {
      setError(false);
      setloading(true);
      // Inside the try: a failed token request shows the send error.
      const accessToken = await getTokenSilently();
      const config = {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };
      await axios.post(flightUrl, requestFlightPayload, config);
      setForm(false);
      setloading(false);
      onSent();
    } catch (error) {
      setError(true);
      setForm(true);
      setloading(false);
      console.log("[contactForm error]", error);
    }
  };

  const submitForm = React.useCallback(() => {
    submitRequest();
  }, []);

  const submitError = React.useCallback(() => {
    methods.trigger();
  }, [methods]);

  const subjectLength = (methods.watch("subject") ?? "").length;
  const descriptionLength = (methods.watch("description") ?? "").length;

  // Same sent state as the Benefits Summary message dialog.
  if (!showForm) {
    return (
      <Stack spacing={3}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <CheckCircleOutlinedIcon color="success" />
          <Typography>
            Thanks for reaching out! Your message has been submitted and a team
            member will be in contact with you shortly.
          </Typography>
        </Stack>
        <Button variant="contained" onClick={onClose} sx={{ alignSelf: "flex-end", minHeight: 44 }}>
          Close
        </Button>
      </Stack>
    );
  }

  return (
    <Stack
      component="form"
      noValidate
      spacing={2.5}
      onSubmit={methods.handleSubmit(submitForm, submitError)}
      aria-label="Contact us"
    >
      {error && (
        <Alert severity="error">
          Message unable to send. Please try again in a few moments or reach out
          to our Customer Support Team.
        </Alert>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <Controller
          name="firstName"
          control={methods.control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ""}
              id="firstName"
              label="First name"
              required
              fullWidth
              autoComplete="given-name"
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
            />
          )}
        />
        <Controller
          name="lastName"
          control={methods.control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value ?? ""}
              id="lastName"
              label="Last name"
              required
              fullWidth
              autoComplete="family-name"
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
            />
          )}
        />
      </Stack>

      <Controller
        name="contactReason"
        control={methods.control}
        render={({ field }) => (
          <TextField
            {...field}
            value={field.value ?? ""}
            id="contactReason"
            label="Reason"
            select
            required
            fullWidth
            error={!!errors.contactReason}
            helperText={errors.contactReason?.message}
          >
            {contactReasons.map((reason) => (
              <MenuItem key={reason.value} value={reason.value}>
                {reason.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      />

      <Controller
        name="subject"
        control={methods.control}
        render={({ field }) => (
          <TextField
            {...field}
            value={field.value ?? ""}
            id="subject"
            label="Subject"
            required
            fullWidth
            error={!!errors.subject}
            helperText={
              errors.subject?.message ?? `${subjectLength}/${maxSubjectLength}`
            }
            slotProps={{
              htmlInput: { maxLength: maxSubjectLength },
              formHelperText: { sx: { textAlign: errors.subject ? "left" : "right" } },
            }}
          />
        )}
      />

      <Controller
        name="description"
        control={methods.control}
        render={({ field }) => (
          <TextField
            {...field}
            value={field.value ?? ""}
            id="description"
            label="How can we help?"
            required
            fullWidth
            multiline
            minRows={4}
            error={!!errors.description}
            helperText={
              errors.description?.message ??
              `${descriptionLength}/${maxDescriptionLength}`
            }
            slotProps={{
              htmlInput: { maxLength: maxDescriptionLength },
              formHelperText: { sx: { textAlign: errors.description ? "left" : "right" } },
            }}
          />
        )}
      />

      <Button type="submit" variant="contained" loading={loading} sx={{ minHeight: 44 }}>
        Send message
      </Button>
    </Stack>
  );
};

export default React.memo(ContactForm);
